import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { restoredScrollTop } from '@/lib/historyPaging';

/** Preserve one visible message while a page is prepended and measured. */
export function useHistoryScrollAnchor(scrollRef: RefObject<HTMLDivElement | null>) {
  const anchor = useRef<{ id: string | null; top: number; fromBottom: number } | null>(null);
  const frame = useRef(0);
  const restoring = useRef(false);
  const cancel = useCallback(() => {
    anchor.current = null;
    restoring.current = false;
    cancelAnimationFrame(frame.current);
    frame.current = 0;
  }, []);
  useEffect(() => cancel, [cancel]);
  const capture = useCallback(() => {
    const el = scrollRef.current;
    if (!el || anchor.current) return false;
    const bounds = el.getBoundingClientRect();
    const row = Array.from(el.querySelectorAll<HTMLElement>('[data-message-id]'))
      .find(node => { const r = node.getBoundingClientRect(); return r.bottom > bounds.top && r.top < bounds.bottom; });
    const saved = { id: row?.dataset.messageId ?? null, top: row ? row.getBoundingClientRect().top - bounds.top : 0, fromBottom: el.scrollHeight - el.scrollTop };
    anchor.current = saved;
    // A failed/no-op request can release its own capture, never a newer one.
    return () => { if (anchor.current === saved) cancel(); };
  }, [scrollRef, cancel]);
  const restore = useCallback(() => {
    const el = scrollRef.current, saved = anchor.current;
    if (!el || !saved || restoring.current) return;
    restoring.current = true;
    el.scrollTop = restoredScrollTop(el.scrollHeight, saved.fromBottom);
    let attempts = 0;
    let stable = 0;
    const align = () => {
      if (anchor.current !== saved) return;
      if (scrollRef.current !== el) { cancel(); return; }
      // Only the bounded rendered window is inspected, during this prepend.
      const row = Array.from(el.querySelectorAll<HTMLElement>('[data-message-id]'))
        .find(node => node.dataset.messageId === saved.id);
      if (row) {
        const delta = row.getBoundingClientRect().top - el.getBoundingClientRect().top - saved.top;
        if (Math.abs(delta) > .5) { el.scrollTop += delta; stable = 0; }
        else stable += 1;
      }
      if (++attempts < 8 && stable < 3) frame.current = requestAnimationFrame(align);
      else cancel();
    };
    frame.current = requestAnimationFrame(align);
  }, [scrollRef, cancel]);
  return { capture, restore, cancel, restoring };
}
